/**
 * PageTip — per-page lightweight tip banner with an optional CTA.
 * Dismissible per-shop via localStorage (so once dismissed, gone for that shop forever).
 *
 * Usage:
 *   <PageTip
 *     id="customers"
 *     icon={Users}
 *     text="Start by adding your first customer with CNIC, photo, and guarantor details."
 *     cta={{ label: "➕ Add Customer", testid: "tip-add-customer-btn", onClick: () => setOpen(true) }}
 *   />
 */
import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { X, Lightbulb } from "lucide-react";

export default function PageTip({ id, icon: Icon = Lightbulb, text, cta }) {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(true); // start hidden, reveal after auth check

  const key = user?.shop_id ? `hp_tip_${id}_${user.shop_id}` : null;

  useEffect(() => {
    if (!key) return;
    setDismissed(localStorage.getItem(key) === "1");
  }, [key]);

  if (dismissed || !user?.shop_id) return null;

  const dismiss = () => {
    if (key) localStorage.setItem(key, "1");
    setDismissed(true);
  };

  return (
    <div
      className="rounded-md border border-sky-200 bg-sky-50/70 px-4 py-3 flex items-start gap-3"
      data-testid={`page-tip-${id}`}
    >
      <div className="rounded-full bg-sky-500/15 p-1.5 shrink-0">
        <Icon className="w-4 h-4 text-sky-700" />
      </div>
      <div className="flex-1 text-sm text-sky-900">
        <div>💡 {text}</div>
        {cta && (
          <button
            type="button"
            onClick={cta.onClick}
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-sky-700 hover:text-sky-900 hover:underline"
            data-testid={cta.testid || `tip-${id}-cta`}
          >
            {cta.label} →
          </button>
        )}
      </div>
      <button
        onClick={dismiss}
        className="text-sky-600/50 hover:text-sky-900 shrink-0 p-1"
        title="Dismiss tip"
        data-testid={`page-tip-${id}-dismiss`}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
